import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { RECIPIENT_TYPE_SELF, TYPE } from 'apps/promo/constants/Constants';
import type { RootState } from 'apps/promo/redux/store';

type FormEntry = {
    fields?: Record<string, unknown>;
    [key: string]: unknown;
};

type FormState = Record<string, FormEntry | undefined>;
type MessagesState = {
    pageMessages?: unknown;
};
type ExtraState = {
    typeOfTransfer?: string;
};

type PageMessagesState = MessagesState['pageMessages'];

export function useForm<TForm = FormEntry>(formName: string): TForm | undefined {
    return useSelector((state: RootState) => {
        const formState = (state as RootState & { form?: FormState }).form;
        return formState?.[formName] as TForm | undefined;
    });
}

export function useForms(): FormState | undefined {
    return useSelector((state: RootState) => (state as RootState & { form?: FormState }).form);
}

export function useLanguage<TLanguage = RootState['language']>(): TLanguage | undefined {
    return useSelector((state: RootState) => state.language as TLanguage | undefined);
}

export function usePageMessages<TMessages = PageMessagesState>(): TMessages | undefined {
    return useSelector((state: RootState) => {
        const messages = (state as RootState & { messages?: MessagesState }).messages;
        return messages?.pageMessages as TMessages | undefined;
    });
}

export function useApp<TApp = RootState['app']>(): TApp | undefined {
    return useSelector((state: RootState) => state.app as TApp | undefined);
}

export function useAuth<TAuth = RootState['auth']>(): TAuth | undefined {
    return useSelector((state: RootState) => state.auth as TAuth | undefined);
}

export function useExtra<TExtra = RootState['extra']>(): TExtra | undefined {
    return useSelector((state: RootState) => state.extra as TExtra | undefined);
}

export function useTypeOfTransfer(): string | null {
    const extra = useSelector((state: RootState) => (state as RootState & { extra?: ExtraState }).extra);
    const [searchParams] = useSearchParams();

    const searchType = searchParams.get(TYPE);
    if (extra?.typeOfTransfer && extra.typeOfTransfer !== searchType && searchType !== RECIPIENT_TYPE_SELF) {
        return extra.typeOfTransfer;
    }
    return searchType;
}
